<script setup>
/**
 * HolidayEditModal
 * Modal for adding/editing/deleting holiday events (Admin only)
 */
import { ref, computed, onMounted } from 'vue'
import AppButton from '@/ui/components/AppButton.vue'
import { useHolidays } from '@/core/composables/useHolidays'
import { useAuthStore } from '@/app/store/auth'
import { useToast } from '@/core/composables/useToast'
import { useConfirmDialog } from '@/core/composables/useConfirmDialog'

const props = defineProps({
  date: {
    type: Date,
    required: true,
  },
  dateKey: {
    type: String,
    required: true,
  },
  initialText: {
    type: String,
    default: '',
  },
})

const emit = defineEmits(['close', 'saved', 'deleted'])

const authStore = useAuthStore()
const { error: toastError } = useToast()
const { confirm } = useConfirmDialog()
const { saveEvent, deleteEvent, saving } = useHolidays()

// Local state
const eventText = ref(props.initialText)

// Formatted date for display
const formattedDate = computed(() => {
  return props.date.toLocaleDateString('he-IL', {
    weekday: 'long',
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  })
})

// Check if has existing event
const hasExistingEvent = computed(() => {
  return !!props.initialText
})

// Handle save
async function handleSave() {
  if (!eventText.value.trim()) {
    toastError('עליך להזין תיאור לאירוע')
    return
  }

  try {
    await saveEvent(props.dateKey, eventText.value.trim(), authStore.userId)
    emit('saved')
  } catch (err) {
    toastError(err.message || 'שגיאה בשמירת האירוע')
  }
}

// Handle delete
async function handleDelete() {
  const ok = await confirm({
    title: 'מחיקת אירוע',
    message: 'האם למחוק את האירוע?',
    confirmText: 'מחק',
    variant: 'danger',
  })
  if (!ok) return

  try {
    await deleteEvent(props.dateKey)
    emit('deleted')
  } catch (err) {
    toastError(err.message || 'שגיאה במחיקת האירוע')
  }
}

// Handle backdrop click
function handleBackdropClick(e) {
  if (e.target === e.currentTarget) {
    emit('close')
  }
}

// Focus textarea on mount
const textareaRef = ref(null)
onMounted(() => {
  setTimeout(() => {
    textareaRef.value?.focus()
  }, 100)
})
</script>

<template>
  <Teleport to="body">
    <div class="modal-overlay" @click="handleBackdropClick">
      <div class="modal-container">
        <!-- Header -->
        <div class="modal-header">
          <h3>עריכת אירוע</h3>
          <button class="close-btn" @click="$emit('close')">
            <svg
              viewBox="0 0 24 24"
              width="20"
              height="20"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <line x1="18" y1="6" x2="6" y2="18" />
              <line x1="6" y1="6" x2="18" y2="18" />
            </svg>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body">
          <div class="date-display">
            <svg
              viewBox="0 0 24 24"
              width="18"
              height="18"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
            >
              <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
              <line x1="16" y1="2" x2="16" y2="6" />
              <line x1="8" y1="2" x2="8" y2="6" />
              <line x1="3" y1="10" x2="21" y2="10" />
            </svg>
            <span>{{ formattedDate }}</span>
          </div>

          <label class="textarea-label">
            <span>תיאור האירוע</span>
            <textarea
              ref="textareaRef"
              v-model="eventText"
              rows="4"
              placeholder="הזן תיאור לאירוע (למשל: חופשת פסח, יום כיפור...)"
              :disabled="saving"
            ></textarea>
          </label>
        </div>

        <!-- Footer -->
        <div class="modal-footer">
          <AppButton variant="secondary" @click="$emit('close')" :disabled="saving">
            ביטול
          </AppButton>

          <AppButton
            v-if="hasExistingEvent"
            variant="danger"
            @click="handleDelete"
            :loading="saving"
          >
            מחק
          </AppButton>

          <AppButton
            variant="primary"
            @click="handleSave"
            :loading="saving"
            :disabled="!eventText.trim()"
          >
            שמור
          </AppButton>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  backdrop-filter: blur(2px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  padding: var(--spacing-md);
  animation: fadeIn 0.2s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

.modal-container {
  background: var(--bg-primary);
  border-radius: var(--border-radius-lg);
  width: 100%;
  max-width: 400px;
  box-shadow: var(--shadow-xl);
  animation: slideIn 0.25s ease;
  overflow: hidden;
}

@keyframes slideIn {
  from {
    opacity: 0;
    transform: translateY(-20px) scale(0.95);
  }
  to {
    opacity: 1;
    transform: translateY(0) scale(1);
  }
}

/* Header */
.modal-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: var(--spacing-md);
  background: linear-gradient(135deg, var(--bg-tertiary) 0%, var(--bg-secondary) 100%);
  border-bottom: 1px solid var(--border-color);
}

.modal-header h3 {
  margin: 0;
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--text-primary);
}

.close-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  background: transparent;
  border: none;
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  cursor: pointer;
  transition: all var(--transition-fast);
}

.close-btn:hover {
  background: var(--bg-tertiary);
  color: var(--text-primary);
}

/* Body */
.modal-body {
  padding: var(--spacing-lg);
  display: flex;
  flex-direction: column;
  gap: var(--spacing-md);
}

.date-display {
  display: flex;
  align-items: center;
  gap: var(--spacing-sm);
  padding: var(--spacing-sm) var(--spacing-md);
  background: var(--bg-secondary);
  border-radius: var(--border-radius);
  color: var(--text-secondary);
  font-size: var(--font-size-sm);
}

.date-display svg {
  color: var(--color-primary);
}

.textarea-label {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-xs);
}

.textarea-label span {
  font-size: var(--font-size-sm);
  font-weight: var(--font-weight-medium);
  color: var(--text-secondary);
}

.textarea-label textarea {
  width: 100%;
  padding: var(--spacing-md);
  border: 2px solid var(--border-color);
  border-radius: var(--border-radius);
  background: var(--bg-primary);
  color: var(--text-primary);
  font-size: var(--font-size-sm);
  font-family: inherit;
  resize: vertical;
  transition: border-color var(--transition-fast);
}

.textarea-label textarea:focus {
  outline: none;
  border-color: var(--color-primary);
}

.textarea-label textarea:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.textarea-label textarea::placeholder {
  color: var(--text-muted);
}

/* Footer */
.modal-footer {
  display: flex;
  justify-content: flex-end;
  gap: var(--spacing-sm);
  padding: var(--spacing-md);
  background: var(--bg-secondary);
  border-top: 1px solid var(--border-color);
}
</style>
